import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IntranetoldService } from 'src/app/api/intranetold.service';
import { ChequeDTO, EfectivoDTO, TarjetaCreditoDTO, TransferenciaDTO } from 'src/app/api/pago.service';
import { TransbankService, TransbankTerminal, TransbankTransaccion } from 'src/app/api/transbank.service';

export interface CobroTbkRescate {
  transaccionId: string;
  authorizationCode: string;
  last4Digits: string;
  cardType: string;
  totalPayment: number;
  sharesNumber: number;
  sharesAmount: number;
  terminalId: string;
  terminalNombre: string;
  fechaIso: string;
  timestamp: number;
  usuarioId?: string;
  usuarioNombre?: string;
}

@Component({
  selector: 'app-pago-modal',
  templateUrl: './pago-modal.component.html',
  styleUrls: ['./pago-modal.component.css']
})
export class PagoModalComponent {
  constructor(private _api: IntranetoldService, public activeModal: NgbActiveModal, private _tbk: TransbankService) {}

  @Input() documentoInfo: { CardCode: string; CardName?: string; DocDate?: string | Date; DocTotalFC?: number } = { CardCode: '' };
  @Input() initialIntent: { cheque?: ChequeDTO[]; transferenciaBancaria?: TransferenciaDTO | null; tarjetaCredito?: TarjetaCreditoDTO[]; efectivo?: EfectivoDTO | null } | null = null;

  // Estado UI
  creandoPago = true;

  // Catálogos
  cuentasBanco: Array<{ CuentaContable: string; NombreBanco?: string; NombreCuentaContable: string }>=[];
  bancosCheques: Array<{ BankCode: string; BankName: string }>=[];
  cuentasOACT: Array<{ AcctCode: string; FatherNum: string; AcctName?: string }>=[];
  posicionesFormulario: Array<{ CFWId: number; CFWName: string }> = [];

  // Modelos
  cheques: ChequeDTO[] = [];
  nuevoCheque: ChequeDTO = { account: '_SYS00000001322', dueDate: '', ammount: 0, bankTransfer: '', endosado: 'NO', checkNum: '' };
  transferenciaBancaria: TransferenciaDTO = { account: '_SYS00000002308', date: '', reference: '', ammount: 0, cashFlowLineItemID: 44 };
  efectivo: EfectivoDTO = { account: '_SYS00000001298', ammount: 0 } as EfectivoDTO;
  tarjetaCredito: TarjetaCreditoDTO[] = [];
  // ── Transbank ────────────────────────────────────────────────────────────
  transbankActivo = false;   // Controlado por el flag en BD — false por defecto
  terminalesTransbank: TransbankTerminal[] = [];
  terminalSeleccionadoId = '';
  tbkEstado: 'inactivo' | 'enviando' | 'esperando' | 'aprobado' | 'rechazado' | 'cancelado' = 'inactivo';
  tbkTransaccionId = '';
  tbkTraceId = '';
  tbkReferencia = '';
  tbkMensaje = '';
  tbkPollingInterval: any = null;
  tbkUltimaTransaccion: TransbankTransaccion | null = null;
  modoCobroTarjeta: 'automatico' | 'manual' = 'manual';
  tarjetaEditIndex: number | null = null;

  nuevaTarjetaCredito: Omit<TarjetaCreditoDTO, 'Number'> = {
    creditCard: 0, account: '', creditCardNumber: '', validUntil: '', ownerIDNumber: '', ownerPhone: '',
    paymentMethod: '', ammountDue: 0, numerOfPayment: 1, firsAmmountPayment: 0, eadAddAmmountPayment: 0, voucherNumebr: '', transactionType: 'S'
  };

  get totalPagado(): number {
    let sum = 0;
    (this.cheques || []).forEach(c => sum += (Number(c.ammount) || 0));
    if (this.transferenciaBancaria?.ammount) sum += (Number(this.transferenciaBancaria.ammount) || 0);
    (this.tarjetaCredito || []).forEach(t => sum += (Number(t.ammountDue) || 0));
    if (this.efectivo?.ammount) sum += (Number(this.efectivo.ammount) || 0);
    return Math.round(sum);
  }

  get docTotal(): number {
    return Math.round(Number(this.documentoInfo?.DocTotalFC) || 0);
  }

  get saldoPendiente(): number {
    const pend = this.docTotal - this.totalPagado;
    return Math.max(0, Math.round(pend));
  }

  async ngOnInit() {
    // Cargar traeBancos
    try {
      const dataQueries: any = await this._api.getQueryAll();
      let sqlTraeBancos = '';
      for (const element of (dataQueries?.objeto || [])) {
        if (element.NOMBRE === 'traeBancos') { sqlTraeBancos = element.CONSULTA; break; }
      }
      if (sqlTraeBancos) {
        const res: any = await this._api.GetNotFileQuery(sqlTraeBancos);
        const rows: any[] = Array.isArray(res?.objeto) ? res.objeto : [];
        this.cuentasBanco = rows.map(r => ({
          CuentaContable: r['CuentaContable'] || r['GLAccount'] || r['AcctCode'] || '',
          NombreBanco: r['NombreBanco'] || r['BankName'] || '',
          NombreCuentaContable: r['NombreCuentaContable'] || r['AcctName'] || ''
        })).filter(x => x.CuentaContable);
      }
    } catch { this.cuentasBanco = []; }
    // Bancos cheque
    try {
      const resBancos: any = await this._api.GetNotFileQuery('SELECT "BankCode", "BankName" FROM ODSC ORDER BY "BankName"');
      this.bancosCheques = Array.isArray(resBancos?.objeto) ? resBancos.objeto : [];
    } catch { this.bancosCheques = []; }
    // OACT
    try {
      const resOact: any = await this._api.GetNotFileQuery('SELECT "AcctCode", "FatherNum", "AcctName" FROM OACT');
      this.cuentasOACT = Array.isArray(resOact?.objeto) ? resOact.objeto : [];
    } catch { this.cuentasOACT = []; }
    // Posiciones formulario principal (OCFW)
    try {
      const dataQueries2: any = await this._api.getQueryAll();
      let sqlPosicion = '';
      for (const element of (dataQueries2?.objeto || [])) {
        if (element.NOMBRE === 'posicionFormularioPrincipal') { sqlPosicion = element.CONSULTA; break; }
      }
      if (sqlPosicion) {
        const resCfw: any = await this._api.GetNotFileQuery(sqlPosicion);
        this.posicionesFormulario = Array.isArray(resCfw?.objeto) ? resCfw.objeto : [];
      }
    } catch { this.posicionesFormulario = []; }

    // Pre-cargar estado si viene un intent inicial (para editar)
    if (this.initialIntent) {
      if (Array.isArray(this.initialIntent.cheque)) this.cheques = [...this.initialIntent.cheque];
      if (this.initialIntent.transferenciaBancaria) this.transferenciaBancaria = { ...this.transferenciaBancaria, ...this.initialIntent.transferenciaBancaria };
      if (Array.isArray(this.initialIntent.tarjetaCredito)) this.tarjetaCredito = [...this.initialIntent.tarjetaCredito];
      if (this.initialIntent.efectivo) this.efectivo = { ...this.efectivo, ...this.initialIntent.efectivo } as EfectivoDTO;
    }

    // Cargar terminales Transbank (solo si el flag está activo para este entorno)
    try {
      this.transbankActivo = await this._tbk.isActivo();
      if (this.transbankActivo) {
        let usuarioLogueado: any = null;
        try {
          const rawUser = localStorage.getItem('LOGIN_USER');
          if (rawUser) usuarioLogueado = JSON.parse(rawUser);
        } catch { }

        let esAdmin = !!(usuarioLogueado?.ADMIN === true);
        if (!esAdmin) {
          try {
            const perfilRaw = localStorage.getItem('PERFILJSON');
            if (perfilRaw) {
              const p = JSON.parse(perfilRaw);
              const pNombre = (p?.NOMBRE || '').toUpperCase();
              if (pNombre.includes('ADMIN') || pNombre.includes('SUPER') || pNombre.includes('DESARROLLO')) {
                esAdmin = true;
              }
            }
          } catch { }
        }
        const resTbk: any = await this._tbk.getTerminales();
        const todosTerminales: TransbankTerminal[] = Array.isArray(resTbk?.objeto) ? resTbk.objeto : [];

        // ── Filtro de Seguridad Producción vs Integración ─────────────────
        // En producción, los usuarios no administradores SOLO ven terminales de PRODUCCION.
        if (!this._tbk.isLocalhost() && !esAdmin) {
          this.terminalesTransbank = todosTerminales.filter(t => t.ambiente === 'PRODUCCION');
        } else {
          this.terminalesTransbank = todosTerminales;
        }

        if (this.terminalesTransbank.length > 0) {
          // ── Jerarquía Inteligente de Selección ───────────────────────────
          // 1. Prioridad: Último terminal guardado en localStorage en este dispositivo
          const ultimoGuardado = localStorage.getItem('ULTIMO_TERMINAL_TBK');
          const existeUltimo = ultimoGuardado ? this.terminalesTransbank.find(t => t._id === ultimoGuardado || t.terminalId === ultimoGuardado) : null;

          // 2. Prioridad: Terminal default asignado en el Maestro de Usuarios
          const defaultUsuario = usuarioLogueado?.TERMINAL_TRANSBANK_DEFAULT;
          const existeDefault = defaultUsuario ? this.terminalesTransbank.find(t => t._id === defaultUsuario || t.terminalId === defaultUsuario) : null;

          if (existeUltimo) {
            this.terminalSeleccionadoId = existeUltimo._id || '';
          } else if (existeDefault) {
            this.terminalSeleccionadoId = existeDefault._id || '';
          } else {
            // 3. Fallback: Primer terminal de la lista disponible
            this.terminalSeleccionadoId = this.terminalesTransbank[0]._id || '';
          }

          this.modoCobroTarjeta = 'automatico';

          // Preestablecer el monto a cobrar con el saldo pendiente del documento
          if (this.saldoPendiente > 0) {
            this.nuevaTarjetaCredito.ammountDue = this.saldoPendiente;
          } else if (this.docTotal > 0) {
            this.nuevaTarjetaCredito.ammountDue = Math.round(this.docTotal);
          }
        } else {
          this.modoCobroTarjeta = 'manual';
        }
      } else {
        this.transbankActivo = false;
        this.terminalesTransbank = [];
        this.modoCobroTarjeta = 'manual';
      }
    } catch {
      this.transbankActivo = false;
      this.terminalesTransbank = [];
      this.modoCobroTarjeta = 'manual';
    }
  }

  onTerminalChange() {
    if (this.terminalSeleccionadoId) {
      localStorage.setItem('ULTIMO_TERMINAL_TBK', this.terminalSeleccionadoId);
    }
  }

  private todayIso(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  onPaymentMethodChange() {
    const method = this.nuevaTarjetaCredito.paymentMethod;
    const accountByMethod: Record<string, string> = { '1': '_SYS00000002266', '2': '_SYS00000002267' };
    this.nuevaTarjetaCredito.account = accountByMethod[method] || '';
    this.nuevaTarjetaCredito.validUntil = this.todayIso();
    if (!this.nuevaTarjetaCredito.transactionType) this.nuevaTarjetaCredito.transactionType = 'S';
  }

  agregarChequeDesdeUI() {
    const c = this.nuevoCheque;
    if (!c || !c.dueDate || !c.ammount) return;
    this.cheques = [...this.cheques, { ...c }];
    this.nuevoCheque = { account: '_SYS00000001322', dueDate: '', ammount: 0, bankTransfer: '', endosado: 'NO', checkNum: '' };
  }
  eliminarCheque(i: number) { this.cheques = this.cheques.filter((_, idx)=> idx !== i); }

  toggleModoCobro() {
    this.modoCobroTarjeta = this.modoCobroTarjeta === 'automatico' ? 'manual' : 'automatico';
  }

  get montoFormateado(): string {
    if (this.nuevaTarjetaCredito.ammountDue === undefined || this.nuevaTarjetaCredito.ammountDue === null || this.nuevaTarjetaCredito.ammountDue === 0) return '';
    return this.formatearMonto(this.nuevaTarjetaCredito.ammountDue);
  }

  formatearMonto(monto: number | undefined | null): string {
    if (monto === undefined || monto === null || isNaN(Number(monto))) return '0';
    const entero = Math.round(Number(monto));
    return entero.toLocaleString('es-CL', { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  onMontoChange(value: string) {
    const numericStr = value.replace(/\D/g, '');
    const num = parseInt(numericStr, 10);
    this.nuevaTarjetaCredito.ammountDue = isNaN(num) ? 0 : num;
  }

  onChangeTarjetaCalculos() {
    const total = Number(this.nuevaTarjetaCredito.ammountDue) || 0;
    const cuotas = Math.max(1, Number(this.nuevaTarjetaCredito.numerOfPayment) || 1);
    const { FirstSum, AddPmntSum } = this.calcularCuotas(total, cuotas);
    this.nuevaTarjetaCredito.firsAmmountPayment = FirstSum;
    this.nuevaTarjetaCredito.eadAddAmmountPayment = AddPmntSum;
  }

  cargarTarjetaParaEdicion(index: number) {
    this.tarjetaEditIndex = index;
    this.nuevaTarjetaCredito = { ...this.tarjetaCredito[index] };
  }

  agregarTarjetaDesdeUI() {
    const t = this.nuevaTarjetaCredito;
    if (!t || !t.account || !t.creditCardNumber || !t.validUntil || !t.ammountDue || !t.creditCard || !t.paymentMethod) return;
    
    if (this.tarjetaEditIndex !== null) {
      this.tarjetaCredito[this.tarjetaEditIndex] = { ...this.tarjetaCredito[this.tarjetaEditIndex], ...t };
      this.tarjetaEditIndex = null;
    } else {
      const index = this.tarjetaCredito.length + 1;
      this.tarjetaCredito = [...this.tarjetaCredito, { Number: index, splitCreditVoucher: false, transactionType: t.transactionType || 'S', ...t }];
    }
    
    this.nuevaTarjetaCredito = { creditCard: 0, account: '', creditCardNumber: '', validUntil: '', ownerIDNumber: '', ownerPhone: '', paymentMethod: '', ammountDue: 0, numerOfPayment: 1, firsAmmountPayment: 0, eadAddAmmountPayment: 0, voucherNumebr: '', transactionType: 'S' };
  }

  eliminarTarjeta(i: number) {
    this.tarjetaCredito = this.tarjetaCredito.filter((_, idx)=> idx !== i);
    this.tarjetaCredito.forEach((tc, idx) => tc.Number = idx + 1);
    if (this.tarjetaEditIndex === i) {
      this.tarjetaEditIndex = null;
    }
    if (this.saldoPendiente > 0) {
      this.nuevaTarjetaCredito.ammountDue = this.saldoPendiente;
    }
  }

  // ── Transbank: Cobrar en terminal físico ─────────────────────────────────

  get terminalSeleccionado(): TransbankTerminal | null {
    return this.terminalesTransbank.find(t => t._id === this.terminalSeleccionadoId) || null;
  }

  async cobrarEnTransbank() {
    const monto = Number(this.nuevaTarjetaCredito.ammountDue);
    if (!monto || monto <= 0) {
      this.tbkMensaje = 'Ingresa un monto válido antes de cobrar.';
      return;
    }
    if (!this.terminalSeleccionadoId) {
      this.tbkMensaje = 'Selecciona un terminal Transbank.';
      return;
    }

    this.tbkEstado = 'enviando';
    this.tbkMensaje = 'Enviando solicitud a la maquinita POS...';
    this.tbkUltimaTransaccion = null;
    this.tbkReferencia = 'PAGO-' + Math.floor(Math.random() * 9000 + 1000);
    this.tbkTraceId = '';

    try {
      const res: any = await this._tbk.cobrar(
        this.terminalSeleccionadoId,
        monto,
        this.tbkReferencia
      );

      if (!res?.estado) {
        this.tbkEstado = 'rechazado';
        this.tbkMensaje = res?.mensaje || 'No se pudo comunicar con el POS.';
        return;
      }

      this.tbkTransaccionId = res.transaccionId;
      this.tbkTraceId = res.traceId || '';
      this.tbkEstado = 'esperando';
      this.tbkMensaje = 'Pasa, inserta o acerca la tarjeta en la maquinita física...';
      this.iniciarPolling();

    } catch (e: any) {
      this.tbkEstado = 'rechazado';
      this.tbkMensaje = e?.message || 'Error de comunicación con el servidor.';
    }
  }

  private iniciarPolling() {
    if (this.tbkPollingInterval) clearInterval(this.tbkPollingInterval);
    this.tbkPollingInterval = setInterval(async () => {
      try {
        const res: any = await this._tbk.consultarResultado(this.tbkTransaccionId);
        const txn: TransbankTransaccion = res?.objeto;
        if (!txn) return;

        const estadoEnVivo = res?.estadoEnVivo || txn.estadoEnVivo || txn.estado || '';

        if (txn.estado === 'PENDIENTE') {
          if (estadoEnVivo.includes('PIN')) {
            this.tbkMensaje = '💳 El cliente está ingresando el PIN en la maquinita...';
          } else if (estadoEnVivo.includes('CUOTA')) {
            this.tbkMensaje = '📊 Seleccionando cuotas en la maquinita...';
          } else if (estadoEnVivo.includes('PROPINA')) {
            this.tbkMensaje = '💰 Seleccionando propina en la maquinita...';
          } else if (estadoEnVivo.includes('VUELTO')) {
            this.tbkMensaje = '💵 Seleccionando vuelto en la maquinita...';
          } else {
            this.tbkMensaje = 'Pasa, inserta o acerca la tarjeta en la maquinita física...';
          }
        } else if (txn.estado === 'APROBADO') {
          clearInterval(this.tbkPollingInterval);
          this.tbkUltimaTransaccion = txn;
          this.tbkTraceId = txn.traceId || this.tbkTraceId;
          this.tbkEstado = 'aprobado';

          let descMonto = `$ ${txn.totalPayment?.toLocaleString('es-CL')}`;
          if (txn.montoPropina && txn.montoPropina > 0) {
            descMonto += ` (Venta: $${(txn.totalPayment - txn.montoPropina).toLocaleString('es-CL')} + Propina: $${txn.montoPropina.toLocaleString('es-CL')})`;
          }
          this.tbkMensaje = `¡Pago Aprobado! ${descMonto} — Voucher #${txn.authorizationCode} — ${TransbankService.describeCardType(txn.cardType)} ****${txn.last4Digits}`;
          this.autoLlenarDesdeTbk(txn);

        } else if (txn.estado === 'RECHAZADO' || txn.estado === 'CANCELADO') {
          clearInterval(this.tbkPollingInterval);
          this.tbkTraceId = txn.traceId || this.tbkTraceId;
          this.tbkEstado = txn.estado === 'RECHAZADO' ? 'rechazado' : 'cancelado';
          this.tbkMensaje = txn.mensajeError || (txn.estado === 'CANCELADO' ? 'Cobro cancelado en el POS.' : 'Transacción rechazada en el POS.');
        }
      } catch { }
    }, 2000);
  }

  private autoLlenarDesdeTbk(txn: TransbankTransaccion) {
    const hoy = this.todayIso();
    const rawInfo = `${txn.cardType || ''} ${txn.ultimoStatusJson || ''} ${txn.imageClientVoucher || ''} ${txn.reference || ''} ${txn.commerceReference || ''}`;
    const paymentMethod = TransbankService.cardTypeToPaymentMethod(txn.cardType, rawInfo);
    const creditCardCode = TransbankService.cardTypeToCreditCardCode(txn.cardType, rawInfo);
    const accountByMethod: Record<string, string> = { '1': '_SYS00000002266', '2': '_SYS00000002267' };

    const nueva: TarjetaCreditoDTO = {
      Number: this.tarjetaCredito.length + 1,
      creditCard: creditCardCode,
      account: accountByMethod[paymentMethod] || '_SYS00000002266',
      creditCardNumber: txn.last4Digits || '0000',
      validUntil: hoy,
      ownerIDNumber: '',
      ownerPhone: '',
      paymentMethod,
      ammountDue: txn.totalPayment,
      numerOfPayment: txn.sharesNumber || 1,
      firsAmmountPayment: txn.sharesAmount || txn.totalPayment,
      eadAddAmmountPayment: txn.sharesAmount || 0,
      voucherNumebr: txn.authorizationCode || txn.reference || '',
      transactionType: 'S',
      splitCreditVoucher: false,
    };

    this.tarjetaCredito = [...this.tarjetaCredito, nueva];

    // Salvaguarda Anti-Pánico: persistir cobro en localStorage para recuperación si falla SAP o se recarga F5
    try {
      let usuarioNombre = '';
      try {
        const u = JSON.parse(localStorage.getItem('LOGIN_USER') || '{}');
        usuarioNombre = u.NOMBRE || '';
      } catch { }

      const rescate: CobroTbkRescate = {
        transaccionId: txn._id || this.tbkTransaccionId || '',
        authorizationCode: txn.authorizationCode || txn.reference || '',
        last4Digits: txn.last4Digits || '0000',
        cardType: txn.cardType || '',
        totalPayment: txn.totalPayment,
        sharesNumber: txn.sharesNumber || 1,
        sharesAmount: txn.sharesAmount || txn.totalPayment,
        terminalId: this.terminalSeleccionadoId,
        terminalNombre: this.terminalSeleccionado?.nombre || '',
        fechaIso: new Date().toISOString(),
        timestamp: Date.now(),
        usuarioId: localStorage.getItem('ID_LOGUEADO') || '',
        usuarioNombre
      };
      localStorage.setItem('TBK_COBRO_PENDIENTE_RECUPERO', JSON.stringify(rescate));
    } catch { }

    // Actualizar monto para el siguiente pago (split) con el saldo pendiente restante
    setTimeout(() => {
      this.nuevaTarjetaCredito.ammountDue = this.saldoPendiente;
    }, 100);
  }

  // ── Métodos de Rescate / Salvaguarda Anti-Pánico ─────────────────────────

  get cobroRescateDisponible(): CobroTbkRescate | null {
    try {
      const raw = localStorage.getItem('TBK_COBRO_PENDIENTE_RECUPERO');
      if (!raw) return null;
      const rescate: CobroTbkRescate = JSON.parse(raw);
      // TTL de 2 horas
      if (!rescate.timestamp || (Date.now() - rescate.timestamp > 2 * 60 * 60 * 1000)) {
        localStorage.removeItem('TBK_COBRO_PENDIENTE_RECUPERO');
        return null;
      }
      return rescate;
    } catch {
      return null;
    }
  }

  tarjetaYaCargada(rescate: CobroTbkRescate | null): boolean {
    if (!rescate) return false;
    return (this.tarjetaCredito || []).some(
      t => String(t.voucherNumebr).trim() === String(rescate.authorizationCode).trim() &&
           Number(t.ammountDue) === Number(rescate.totalPayment)
    );
  }

  minutosAtras(timestamp: number): number {
    if (!timestamp) return 0;
    const diff = Math.floor((Date.now() - timestamp) / 60000);
    return Math.max(0, diff);
  }

  aplicarCobroRescate() {
    const rescate = this.cobroRescateDisponible;
    if (!rescate) return;
    const hoy = this.todayIso();
    const rawInfo = `${rescate.cardType || ''} ${rescate.authorizationCode || ''}`;
    const paymentMethod = TransbankService.cardTypeToPaymentMethod(rescate.cardType, rawInfo);
    const creditCardCode = TransbankService.cardTypeToCreditCardCode(rescate.cardType, rawInfo);
    const accountByMethod: Record<string, string> = { '1': '_SYS00000002266', '2': '_SYS00000002267' };

    const nueva: TarjetaCreditoDTO = {
      Number: this.tarjetaCredito.length + 1,
      creditCard: creditCardCode,
      account: accountByMethod[paymentMethod] || '_SYS00000002266',
      creditCardNumber: rescate.last4Digits || '0000',
      validUntil: hoy,
      ownerIDNumber: '',
      ownerPhone: '',
      paymentMethod,
      ammountDue: rescate.totalPayment,
      numerOfPayment: rescate.sharesNumber || 1,
      firsAmmountPayment: rescate.sharesAmount || rescate.totalPayment,
      eadAddAmmountPayment: rescate.sharesAmount || 0,
      voucherNumebr: rescate.authorizationCode || '',
      transactionType: 'S',
      splitCreditVoucher: false,
    };

    this.tarjetaCredito = [...this.tarjetaCredito, nueva];
    if (this.saldoPendiente > 0) {
      this.nuevaTarjetaCredito.ammountDue = this.saldoPendiente;
    }
  }

  descartarCobroRescate() {
    localStorage.removeItem('TBK_COBRO_PENDIENTE_RECUPERO');
  }

  async cancelarTransbank() {
    if (this.tbkEstado === 'esperando' && this.tbkTransaccionId) {
      this.tbkMensaje = 'Cancelando cobro en el POS...';
      try {
        await this._tbk.anular(this.tbkTransaccionId);
      } catch (e) {
        console.error('Error al anular transaccion', e);
      }
    }
    if (this.tbkPollingInterval) clearInterval(this.tbkPollingInterval);
    this.tbkEstado = 'inactivo';
    this.tbkMensaje = '';
    this.tbkTransaccionId = '';
  }

  get hasTarjetaPendiente(): boolean {
    try { const m = this.nuevaTarjetaCredito?.paymentMethod; const amt = Number(this.nuevaTarjetaCredito?.ammountDue) || 0; return !!(m && amt > 0); } catch { return false; }
  }

  private calcularCuotas(ammountDue: number, cuotas: number) {
    let FirstSum = 0; let AddPmntSum = 0;
    if (!cuotas || cuotas <= 1) { FirstSum = Number(ammountDue) || 0; AddPmntSum = 0; }
    else { const aux = (Number(ammountDue) || 0) / cuotas; if (Math.floor(aux) !== aux) { AddPmntSum = Math.floor(aux); const parcial = AddPmntSum * (cuotas - 1); FirstSum = (Number(ammountDue) || 0) - parcial; } else { FirstSum = aux; AddPmntSum = aux; } }
    return { FirstSum, AddPmntSum };
  }

  // Labels visuales FatherNum
  private findFatherNum(acctCode?: string): string {
    if (!acctCode) return '';
    const row = this.cuentasOACT?.find(x => String(x.AcctCode).trim() === String(acctCode).trim());
    return row?.FatherNum || String(acctCode);
  }
  private findAcctName(acctCode?: string): string {
    if (!acctCode) return '';
    const row = this.cuentasOACT?.find(x => String(x.AcctCode).trim() === String(acctCode).trim());
    return row?.AcctName || '';
  }
  get efectivoCuentaLabel() { return this.findFatherNum(this.efectivo?.account); }
  get chequeCuentaLabel() { return this.findFatherNum(this.nuevoCheque?.account); }
  get tarjetaCuentaLabel() { return this.findFatherNum(this.nuevaTarjetaCredito?.account); }
  get efectivoCuentaName() { return this.findAcctName(this.efectivo?.account); }
  get chequeCuentaName() { return this.findAcctName(this.nuevoCheque?.account); }
  get tarjetaCuentaName() { return this.findAcctName(this.nuevaTarjetaCredito?.account); }

  cancelar() { this.activeModal.dismiss(); }

  confirmar() {
    // Incluir tarjeta pendiente si aplica
    const tarjetasFuente = (this.tarjetaCredito && this.tarjetaCredito.length)
      ? this.tarjetaCredito
      : (this.hasTarjetaPendiente ? [{ Number: 1, splitCreditVoucher: false, transactionType: this.nuevaTarjetaCredito.transactionType || 'S', ...this.nuevaTarjetaCredito }] as TarjetaCreditoDTO[] : []);
    const intent = {
      cheque: (this.cheques || []).filter(c => Number(c.ammount) > 0),
      transferenciaBancaria: (this.transferenciaBancaria && this.transferenciaBancaria.account && Number(this.transferenciaBancaria.ammount) > 0) ? this.transferenciaBancaria : null,
      tarjetaCredito: (tarjetasFuente || []).filter(t => Number(t.ammountDue) > 0),
      efectivo: (this.efectivo && this.efectivo.account && Number(this.efectivo.ammount) > 0) ? this.efectivo : null,
    };
    this.activeModal.close(intent);
  }
}
